import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { axiosInstance } from '../../api/apiConfig';
import { Form, Button, Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { User, Phone, Calendar, School, Home, Users, CreditCard, Image } from 'lucide-react';
import { format } from 'date-fns';

// A function (not a constant) so start_date is fresh on mount and on reset.
const getInitialFormState = () => ({
  full_name: '',
  phone_number: '',
  emergency_phone_number: '',
  photo: '',
  id_photo: '',
  gender: '',
  start_date: format(new Date(), 'yyyy-MM-dd'),
  end_date: '',
  registration_number: '',
  year_of_study: 1,
  id_number: '',
  sponsor_phone: '',
  emergency_contact_relationship: '',
  room: '',
  college: '',
  faculty: '',
  terms: false,
});

const FIELD_LABELS = {
  full_name: 'Full name',
  phone_number: 'Phone number',
  emergency_phone_number: 'Emergency phone number',
  photo: 'Your photo',
  id_photo: 'ID photo',
  gender: 'Gender',
  start_date: 'Start date',
  end_date: 'End date',
  registration_number: 'Registration number',
  year_of_study: 'Year of study',
  id_number: 'ID number',
  sponsor_phone: 'Sponsor phone',
  emergency_contact_relationship: 'Relationship',
  room: 'Room',
  college: 'College',
  faculty: 'Faculty',
  terms: 'Terms and conditions',
};

// Turns a DRF error response into readable lines.
const formatApiError = (error) => {
  const data = error.response?.data;
  if (!data || typeof data !== 'object') {
    return 'Could not submit the form. Check your connection and try again.';
  }
  if (typeof data.detail === 'string') return data.detail;
  return Object.entries(data)
    .map(([field, messages]) => `${FIELD_LABELS[field] || field}: ${[].concat(messages).join(' ')}`)
    .join('\n');
};

const OnboardingForm = () => {
  // May be a slug, an ID, or a company name. Only the backend resolves it.
  const { companyIdentifier } = useParams();

  const [company, setCompany] = useState(null);
  const [formData, setFormData] = useState(getInitialFormState);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [rooms, setRooms] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [idPhotoPreview, setIdPhotoPreview] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingFaculties, setLoadingFaculties] = useState(false);
  const [accountNumber, setAccountNumber] = useState(null);

  // Resolve the company from whatever identifier is in the URL
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setIsLoading(true);
        setLoadError('');
        const response = await axiosInstance.get(
          `onboard/api/${encodeURIComponent(companyIdentifier)}/`
        );
        setCompany(response.data);
      } catch (error) {
        console.error('Error fetching company:', error);
        setCompany(null);
        setLoadError(
          error.response?.status === 404
            ? 'We could not find that company. Check the link you were given.'
            : 'Could not load company information. Please try again later.'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompany();
  }, [companyIdentifier]);

  // Everything below uses company.slug, never the URL value
  useEffect(() => {
    if (!company) return;

    const fetchRooms = async () => {
      try {
        setLoadingRooms(true);
        const response = await axiosInstance.get(`onboard/rooms/${company.slug}/`);
        setRooms(response.data);
      } catch (error) {
        console.error('Error fetching rooms:', error);
      } finally {
        setLoadingRooms(false);
      }
    };

    const fetchColleges = async () => {
      try {
        const response = await axiosInstance.get('onboard/get_colleges/');
        setColleges(response.data);
      } catch (error) {
        console.error('Error fetching colleges:', error);
      }
    };

    fetchRooms();
    fetchColleges();
  }, [company]);

  useEffect(() => {
    if (!formData.college) {
      setFaculties([]);
      return;
    }

    const fetchFaculties = async () => {
      try {
        setLoadingFaculties(true);
        const response = await axiosInstance.get(`onboard/get_faculties/${formData.college}/`);
        setFaculties(response.data.faculties);
      } catch (error) {
        console.error('Error fetching faculties:', error);
      } finally {
        setLoadingFaculties(false);
      }
    };

    fetchFaculties();
  }, [formData.college]);

  // Derived, so it resets automatically when the form resets
  const selectedRoom = rooms.find((room) => String(room.id) === String(formData.room));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // A faculty from the previous college is no longer valid
      ...(name === 'college' ? { faculty: '' } : {}),
    }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    const setPreview = field === 'photo' ? setPhotoPreview : setIdPhotoPreview;

    if (!file) {
      setFormData((prev) => ({ ...prev, [field]: '' }));
      setPreview(null);
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.terms) {
      setErrorMessage('You must agree to the terms and conditions.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSubmitSuccess(false);

    try {
      const body = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        // Skip empty strings so missing files/optional fields aren't sent as ""
        if (value !== '' && value !== null && value !== undefined) {
          body.append(key, value);
        }
      });
      // No 'company' field: the backend takes it from the URL.
      // No manual Content-Type: axios sets multipart with the right boundary.

      const response = await axiosInstance.post(
        `onboard/api/company/${company.slug}/onboard/student/`,
        body
      );

      setAccountNumber(response.data.account_number);
      setSubmitSuccess(true);

      setFormData(getInitialFormState());
      setPhotoPreview(null);
      setIdPhotoPreview(null);
      setFileInputKey((key) => key + 1); // clears the file inputs
    } catch (error) {
      console.error('Submission error:', error);
      setErrorMessage(formatApiError(error));
    } finally {
      setIsSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' }); // alerts are at the top
    }
  };

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" variant="primary" />
          <p className="mt-3">Loading company information...</p>
        </div>
      </Container>
    );
  }

  if (!company) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{loadError || 'Company not found.'}</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card className="shadow-sm">
        <Card.Header className="bg-secondary text-white">
          <div className="d-flex align-items-center">
            {company.logo && (
              <img
                src={company.logo}
                alt={company.name}
                className="me-3"
                style={{ height: '50px', maxWidth: '100px', objectFit: 'contain' }}
              />
            )}
            <h2 className="mb-0">{company.name} student onboarding</h2>
          </div>
        </Card.Header>

        <Card.Body>
          {errorMessage && (
            <Alert variant="danger" style={{ whiteSpace: 'pre-line' }}>
              {errorMessage}
            </Alert>
          )}
          {submitSuccess && (
            <Alert variant="success">
              <strong>Success!! <br /> Your account number is: {accountNumber}</strong>
            </Alert>
          )}

          <Form onSubmit={handleSubmit} encType="multipart/form-data">
            <h4 className="mb-3">Personal Information</h4>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <User size={16} className="me-2" />
                    Full Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <CreditCard size={16} className="me-2" />
                    ID Number
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="id_number"
                    value={formData.id_number}
                    onChange={handleInputChange}
                    placeholder="Enter your ID number"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <Phone size={16} className="me-2" />
                    Phone Number
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    placeholder="e.g. 0712345678"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Gender</Form.Label>
                  <div className="d-flex">
                    <Form.Check
                      type="radio"
                      id="male"
                      name="gender"
                      value="male"
                      checked={formData.gender === 'male'}
                      onChange={handleInputChange}
                      label="Male"
                      className="me-3"
                      required
                    />
                    <Form.Check
                      type="radio"
                      id="female"
                      name="gender"
                      value="female"
                      checked={formData.gender === 'female'}
                      onChange={handleInputChange}
                      label="Female"
                      required
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <h4 className="mb-3 mt-4">Emergency Contact</h4>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <Phone size={16} className="me-2" />
                    Emergency Phone Number
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="emergency_phone_number"
                    value={formData.emergency_phone_number}
                    onChange={handleInputChange}
                    placeholder="Emergency contact phone"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <Users size={16} className="me-2" />
                    Relationship
                  </Form.Label>
                  <Form.Select
                    name="emergency_contact_relationship"
                    value={formData.emergency_contact_relationship}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select relationship</option>
                    <option value="Parent">Parent</option>
                    <option value="Guardian">Guardian</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <Phone size={16} className="me-2" />
                    Sponsor Phone
                    <h6 className="small text-muted">!One who pays your rent </h6>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="sponsor_phone"
                    value={formData.sponsor_phone}
                    onChange={handleInputChange}
                    placeholder="Sponsor's phone number"
                  />
                </Form.Group>
              </Col>
            </Row>

            <h4 className="mb-3 mt-4">Academic Information</h4>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <School size={16} className="me-2" />
                    Registration Number
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="registration_number"
                    value={formData.registration_number}
                    onChange={handleInputChange}
                    placeholder="Your student registration number"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <School size={16} className="me-2" />
                    Year of Study
                  </Form.Label>
                  <Form.Select
                    name="year_of_study"
                    value={formData.year_of_study}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="1">Year 1</option>
                    <option value="2">Year 2</option>
                    <option value="3">Year 3</option>
                    <option value="4">Year 4</option>
                    <option value="5">Year 5</option>
                    <option value="6">Year 6</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <School size={16} className="me-2" />
                    College
                  </Form.Label>
                  <Form.Select
                    name="college"
                    value={formData.college}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select College</option>
                    {colleges.map((college) => (
                      <option key={college.id} value={college.id}>
                        {college.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <School size={16} className="me-2" />
                    Faculty
                  </Form.Label>
                  <Form.Select
                    name="faculty"
                    value={formData.faculty}
                    onChange={handleInputChange}
                    required
                    disabled={!formData.college || loadingFaculties}
                  >
                    {loadingFaculties ? (
                      <option>Loading faculties...</option>
                    ) : (
                      <>
                        <option value="">Select Faculty</option>
                        {faculties.map((faculty) => (
                          <option key={faculty.id} value={faculty.id}>
                            {faculty.name}
                          </option>
                        ))}
                      </>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h4 className="mb-3 mt-4">Accommodation Details</h4>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <Home size={16} className="me-2" />
                    Room
                  </Form.Label>
                  <Form.Select
                    name="room"
                    value={formData.room}
                    onChange={handleInputChange}
                    required
                    disabled={loadingRooms}
                  >
                    {loadingRooms ? (
                      <option>Loading rooms...</option>
                    ) : (
                      <>
                        <option value="">Select Room</option>
                        {rooms.map((room) => (
                          <option key={room.id} value={room.id}>
                            {`Room ${room.room_number}`}
                          </option>
                        ))}
                      </>
                    )}
                  </Form.Select>
                  {selectedRoom && (
                    <div className="mt-2">
                      <span className="badge bg-success">
                        Rate: KSh {Number(selectedRoom.rate).toLocaleString()}
                      </span>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <Calendar size={16} className="me-2" />
                    Start Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <Calendar size={16} className="me-2" />
                    End Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <h4 className="mb-3 mt-4">Documents</h4>
            <Row className="mb-4">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <Image size={16} className="me-2" />
                    Your Photo
                  </Form.Label>
                  <Form.Control
                    key={`photo-${fileInputKey}`}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'photo')}
                    required
                  />
                  {photoPreview && (
                    <div className="mt-2">
                      <img
                        src={photoPreview}
                        alt="Preview"
                        style={{ height: '100px', objectFit: 'cover' }}
                        className="border rounded"
                      />
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <CreditCard size={16} className="me-2" />
                    ID Photo
                  </Form.Label>
                  <Form.Control
                    key={`id-photo-${fileInputKey}`}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'id_photo')}
                    required
                  />
                  {idPhotoPreview && (
                    <div className="mt-2">
                      <img
                        src={idPhotoPreview}
                        alt="ID Preview"
                        style={{ height: '100px', objectFit: 'cover' }}
                        className="border rounded"
                      />
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <h4 className="mb-3 mt-4">Agreement</h4>
            <Row className="mb-3">
              <Col>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="terms"
                    name="terms"
                    checked={formData.terms}
                    onChange={(e) => setFormData((prev) => ({ ...prev, terms: e.target.checked }))}
                    label={
                      <span>
                        I agree to the{' '}
                        <a
                          href="https://sunshine.pythonanywhere.com/onboard/terms/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'underline' }}
                        >
                          terms and conditions
                        </a>
                      </span>
                    }
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-grid gap-2 d-md-flex justify-content-md-end">
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  'Submit Registration'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default OnboardingForm;